import {TInventory} from '@app/types';
import {TableProps} from 'antd';
import {useSearchParams} from 'react-router-dom';
import {LogEvents, useSendEvent} from '@app/utils/logger';
import {useCallback, useState} from 'react';
import {useDeleteInventoryMutation} from '@app/redux/services/inventoryService';
import {t} from 'i18next';
import showNotification from '@app/utils/showNotification';

interface IUseInventoryListState {
  selectedInventoryDate: string | undefined;
  isDeleteModalOpen: boolean;
  isDeleteModalClose: boolean;
  isMemoModalOpen: boolean;
  isMemoModalClose: boolean;
}
interface IUseInventoryListActions {
  onTableChange: TableProps<TInventory>['onChange'];
  onDeleteModalOpen: (inventoryDate: string) => void;
  onDeleteModalClose: () => void;
  onDeleteConfirm: () => void;
  onMemoModalOpen: (inventoryDate: string) => void;
  onMemoModalClose: () => void;
}

const useInventoryList = (): [
  IUseInventoryListState,
  IUseInventoryListActions,
] => {
  const [state, setState] = useState<IUseInventoryListState>({
    selectedInventoryDate: '',
    isDeleteModalOpen: false,
    isMemoModalOpen: false,
    isDeleteModalClose: false,
    isMemoModalClose: false,
  });
  const sendEvent = useSendEvent();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteInventory] = useDeleteInventoryMutation();

  const onTableChange: TableProps<TInventory>['onChange'] = (pagination) => {
    const {current, pageSize} = pagination;
    sendEvent(LogEvents.INVENTORY_LIST_TABLE_PAGINATE);
    searchParams.set('page', String(current));
    searchParams.set('limit', String(pageSize));
    setSearchParams(searchParams);
  };
  const onMemoModalOpen = useCallback((inventoryDate: string) => {
    setState({
      selectedInventoryDate: inventoryDate,
      isDeleteModalOpen: false,
      isDeleteModalClose: true,
      isMemoModalOpen: true,
      isMemoModalClose: false,
    });
  }, []);
  const onMemoModalClose = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      selectedInventoryDate: undefined,
      isMemoModalOpen: false,
      isDeleteModalOpen: false,
    }));
  }, []);
  const onDeleteModalOpen = useCallback((inventoryDate: string) => {
    setState({
      selectedInventoryDate: inventoryDate,
      isMemoModalOpen: false,
      isMemoModalClose: true,
      isDeleteModalOpen: true,
      isDeleteModalClose: false,
    });
  }, []);
  const onDeleteModalClose = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      selectedInventoryDate: undefined,
      isMemoModalOpen: false,
      isDeleteModalOpen: false,
    }));
  }, []);
  const onDeleteConfirm = useCallback(async () => {
    if (!state.selectedInventoryDate) return;

    try {
      sendEvent(LogEvents.INVENTORY_DELETE_PRESS, {
        inventoryDate: state.selectedInventoryDate,
      });
      await deleteInventory(state.selectedInventoryDate).unwrap();
      showNotification(
        t('Success'),
        t('Successfully deleted!'),
        'bottomRight',
        'success',
      );
    } catch (error) {
      showNotification(
        t('Error'),
        t('Failed to delete inventory. Please try again.'),
        'bottomRight',
        'error',
      );
    } finally {
      onDeleteModalClose();
    }
  }, [
    state.selectedInventoryDate,
    sendEvent,
    deleteInventory,
    t,
    onDeleteModalClose,
  ]);
  const actions: IUseInventoryListActions = {
    onTableChange,
    onDeleteModalOpen,
    onDeleteModalClose,
    onDeleteConfirm,
    onMemoModalOpen,
    onMemoModalClose,
  };

  return [state, actions];
};

export default useInventoryList;
